import React from "react";
import { Link } from "react-router-dom";
import "../login.scss";
import "../NgoRegistration/orgSignup.scss";
import { KaporgIcon } from "../../../assets/icons";
import ShowcaseCard from "./ShowcaseCard";
import LanguageSwitcher from "../../../../_metronic/layout/components/subheader/components/LanguageSwitcher";
import { useTranslation } from "../../../i18n/LanguageContext";

// autoComplete for searchable antd Selects. Chrome ignores "off" (antd's default) and
// fills the search box with a saved address value (e.g. the country), which leaves typed
// text in the box without selecting anything. An unrecognised token turns autofill off.
export const NO_AUTOFILL = "kap-no-autofill";

export const ArrowIcon = ({ direction = "right" }) => (
   <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
         d={
            direction === "right"
               ? "M2 7H12M12 7L7.5 2.5M12 7L7.5 11.5"
               : "M12 7H2M2 7L6.5 2.5M2 7L6.5 11.5"
         }
         stroke="currentColor"
         strokeWidth="1.4"
         strokeLinecap="round"
         strokeLinejoin="round"
      />
   </svg>
);

const CheckIcon = () => (
   <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
         d="M2.5 6.5L5 9L9.5 3.5"
         stroke="currentColor"
         strokeWidth="1.6"
         strokeLinecap="round"
         strokeLinejoin="round"
      />
   </svg>
);

/** "1 Account —— 2 Organization —— 3 Contact"; finished steps show a check. */
export const SignupStepper = ({ steps, current }) => (
   <ol className="kap-org-signup__steps">
      {steps.map((label, index) => (
         <li
            key={label}
            className={`kap-org-signup__step${
               index <= current ? " kap-org-signup__step--active" : ""
            }`}
            aria-current={index === current ? "step" : undefined}
         >
            <span className="kap-org-signup__step-number">
               {index < current ? <CheckIcon /> : index + 1}
            </span>
            <span className="kap-org-signup__step-label">{label}</span>
         </li>
      ))}
   </ol>
);

/**
 * Touches and validates only `fields` (the current step's), so later steps don't open
 * already red. Resolves true when the step is valid and the wizard can move on.
 */
export const validateSignupStep = async ({ validateForm, setTouched, touched }, fields) => {
   setTouched({ ...touched, ...Object.fromEntries(fields.map((f) => [f, true])) });
   const errors = await validateForm();
   return !fields.some((f) => errors[f]);
};

/**
 * onKeyDown for a wizard <Form>: on any step but the last, Enter means "Continue"
 * instead of submitting the whole form. Inside antd dropdowns Enter picks an option,
 * so there it only blocks the submit.
 */
export const wizardEnterHandler = (isLastStep, onContinue) => (e) => {
   if (e.key !== "Enter" || isLastStep || e.target.tagName === "TEXTAREA") {
      return;
   }
   e.preventDefault();
   if (!e.target.closest(".ant-select, .ant-picker")) {
      onContinue();
   }
};

/**
 * Split layout shared by the sign-up forms and their verification step: the image card
 * on the left (same dimensions as login), the page content on the right under a
 * "Back" + logo header. Pass `backTo` for a link or `onBack` for a handler.
 */
function SignupLayout({ showcase, backTo, onBack, children }) {
   const { t } = useTranslation();

   const backContent = (
      <>
         <ArrowIcon direction="left" />
         {t("back-button")}
      </>
   );

   return (
      <div className="kap-login">
         <ShowcaseCard name={showcase} className="kap-org-signup__showcase" />
         <div className="kap-login__content">
            <div className="kap-org-signup">
               <div className="kap-org-signup__header">
                  {onBack ? (
                     <button type="button" className="kap-org-signup__back" onClick={onBack}>
                        {backContent}
                     </button>
                  ) : (
                     <Link to={backTo} className="kap-org-signup__back">
                        {backContent}
                     </Link>
                  )}
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                     {process.env.REACT_APP_BASE_URL === "https://stagingv2api.kaporg.com/api" && (
                        <LanguageSwitcher color="#4158CF" />
                     )}
                     <Link to="/">
                        <img src={KaporgIcon} alt="KAPorg" className="kap-org-signup__logo" />
                     </Link>
                  </div>
               </div>
               {children}
            </div>
         </div>
      </div>
   );
}

export default SignupLayout;
